import { FC, useState, useEffect, KeyboardEvent } from 'react'

import { styled } from 'styled-components'

import { humanizeKey } from 'peach/helpers'

import { Switch, TextInput } from 'core/components'
import Icon from 'core/components/Icon'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'core/components/lib/Popover'
import { MultiSelect, Select } from 'core/components/lib/Select'
import { R } from 'core/helpers'

import { getFilters } from './registry'
import { FilterFieldSchema, FilterSchema } from './types'
import { buildClearPatch, isActive } from './helpers'

export type AutoFiltersProps = {
  /**
   * Generated operation (e.g. `internalUsersGet`). Its `name` is used to
   * look up the registered `FilterSchema`. Either `operation` or `schema`
   * must be provided.
   */
  operation?: { name: string }
  /** Provide a schema explicitly instead of (or alongside) the registry. */
  schema?: FilterSchema
  /** Current query values (typically from `useQueryString`). */
  query: Record<string, unknown> | undefined
  /**
   * Default query values (typically the same object passed to `useQueryString`).
   * A field is only considered "active" — and only counted in the badge — when
   * its value differs from the default. Clearing a field resets it to its
   * default (or removes it from the URL when no default is provided).
   */
  defaults?: Record<string, unknown>
  /**
   * Called with a patch of changed keys. Pass-through to the page's query
   * setter (e.g. `setQuery` from `useQueryString`).
   * Use `null` to clear a value from the URL.
   */
  onChange: (patch: Record<string, unknown>) => void
  /** Filter keys to hide from the panel. */
  hide?: ReadonlyArray<string>
  /** Per-field overrides merged on top of the registered schema. */
  overrides?: Record<string, Partial<FilterFieldSchema>>
  className?: string
}

const AutoFilters: FC<AutoFiltersProps> = ({
  operation,
  schema: explicitSchema,
  query,
  defaults,
  onChange,
  hide,
  overrides,
  className,
}) => {
  const [isOpen, setOpen] = useState(false)

  const registered = operation ? getFilters(operation.name) : undefined
  const baseSchema = explicitSchema ?? registered

  const hidden = hide ?? []
  const entries = baseSchema
    ? R.pipe(
        R.toPairs(baseSchema),
        R.filter(([key]) => !hidden.includes(key)),
      )
    : []

  const activeCount = R.pipe(
    entries,
    R.filter(([key]) => isActive(query?.[key], defaults?.[key])),
    R.length(),
  )

  if (!baseSchema || entries.length === 0) return null

  const clearAll = () => {
    onChange(
      buildClearPatch(
        R.map(entries, ([key]) => key),
        defaults,
      ),
    )
  }

  return (
    <Popover onOpenChange={setOpen} open={isOpen}>
      <PopoverTrigger asChild>
        <Trigger $active={activeCount > 0} className={className} type='button'>
          <span>Filters</span>
          {activeCount > 0 && <Badge>{activeCount}</Badge>}
          <Icon
            fontSize='18px'
            name={isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
          />
        </Trigger>
      </PopoverTrigger>

      <StyledPopoverContent align='end' side='bottom' sideOffset={6}>
        <PanelHeader>
          <PanelTitle>Filters</PanelTitle>
          {activeCount > 0 && (
            <ClearAll onClick={clearAll} type='button'>
              Clear all
            </ClearAll>
          )}
        </PanelHeader>

        <Rows>
          {entries.map(([key, rawField]) => {
            const field = {
              ...rawField,
              ...(overrides?.[key] ?? {}),
            } as FilterFieldSchema
            const label = field.label ?? humanizeKey(key)
            const value = query?.[key]

            return (
              <Row key={key}>
                <RowLabel htmlFor={`auto-filter-${key}`}>{label}</RowLabel>
                <RowControl>
                  <FieldControl
                    field={field}
                    fieldKey={key}
                    onChange={(next) => onChange({ [key]: next })}
                    value={value}
                  />
                </RowControl>
              </Row>
            )
          })}
        </Rows>
      </StyledPopoverContent>
    </Popover>
  )
}

export default AutoFilters

type FieldControlProps = {
  fieldKey: string
  field: FilterFieldSchema
  value: unknown
  onChange: (next: unknown) => void
}

const FIELD_WIDTH = '180px'

const FieldControl: FC<FieldControlProps> = ({
  fieldKey,
  field,
  value,
  onChange,
}) => {
  if (field.type === 'enum') {
    const options = R.map(field.enum, (v) => ({
      value: v,
      label: field.optionLabels?.[v] ?? humanizeKey(v),
    }))

    if (field.multi) {
      const current = Array.isArray(value) ? (value as Array<string>) : []
      return (
        <MultiSelect
          inputId={`auto-filter-${fieldKey}`}
          isClearable
          isSearchable={false}
          onChange={(next) => onChange(next && next.length > 0 ? next : null)}
          options={options}
          placeholder='Any'
          value={current}
          width={FIELD_WIDTH}
        />
      )
    }

    return (
      <Select
        inputId={`auto-filter-${fieldKey}`}
        isClearable
        isSearchable={false}
        onChange={(next) => onChange(next ?? null)}
        options={options}
        placeholder='Any'
        value={typeof value === 'string' ? value : null}
        width={FIELD_WIDTH}
      />
    )
  }

  if (field.type === 'boolean') {
    return (
      <Switch
        checked={value === true || value === 'true'}
        id={`auto-filter-${fieldKey}`}
        name={fieldKey}
        onCheckedChange={(checked) => onChange(checked || null)}
      />
    )
  }

  if (field.type === 'number' || field.type === 'integer') {
    return (
      <DebouncedTextInput
        format={field.type === 'integer' ? 'integer' : 'float'}
        id={`auto-filter-${fieldKey}`}
        name={fieldKey}
        onCommit={(next) => onChange(next === '' ? null : Number(next))}
        placeholder={field.placeholder ?? 'Any'}
        value={value == null ? '' : String(value)}
      />
    )
  }

  if (field.type === 'string') {
    return (
      <DebouncedTextInput
        id={`auto-filter-${fieldKey}`}
        name={fieldKey}
        onCommit={(next) => onChange(next === '' ? null : next)}
        placeholder={field.placeholder ?? 'Any'}
        type={field.format === 'date' ? 'date' : 'text'}
        value={typeof value === 'string' ? value : ''}
      />
    )
  }

  return null
}

type DebouncedTextInputProps = {
  id: string
  name: string
  value: string
  placeholder?: string
  format?: 'integer' | 'float'
  type?: string
  onCommit: (next: string) => void
}

const DebouncedTextInput: FC<DebouncedTextInputProps> = ({
  id,
  name,
  value,
  placeholder,
  format,
  type,
  onCommit,
}) => {
  const [draft, setDraft] = useState(value)

  useEffect(() => {
    setDraft(value)
  }, [value])

  const commit = () => {
    if (draft !== value) onCommit(draft)
  }

  return (
    <TextInput
      format={format}
      id={id}
      name={name}
      onBlur={commit}
      onChange={setDraft}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') commit()
      }}
      placeholder={placeholder}
      type={type}
      value={draft}
      variant='outline'
      width={FIELD_WIDTH}
    />
  )
}

const Trigger = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  gap: 6px;
  align-items: center;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 6px;
  background-color: ${(p) =>
    p.$active ? p.theme.highlight : p.theme.background};
  cursor: pointer;
  padding: 6px 10px;
  line-height: 1;
  color: ${(p) => (p.$active ? p.theme.invertedText : p.theme.text)};
  font-family: inherit;
  font-size: 13px;

  &:hover {
    border-color: ${(p) => p.theme.highlight};
  }

  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.highlight};
    outline-offset: 1px;
  }
`

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background-color: ${(p) => p.theme.primary};
  padding: 0 6px;
  min-width: 18px;
  height: 18px;
  color: white;
  font-size: 11px;
  font-weight: 600;
`

const StyledPopoverContent = styled(PopoverContent)`
  z-index: 50;
  box-shadow: 0 10px 30px rgb(0 0 0 / 18%);
  padding: 8px 0;
  min-width: 320px;
  max-width: 480px;
`

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${(p) => p.theme.border};
  padding: 4px 12px 8px;
`

const PanelTitle = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(p) => p.theme.mutedText};
  font-size: 11px;
  font-weight: 600;
`

const ClearAll = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  color: ${(p) => p.theme.highlight};
  font-family: inherit;
  font-size: 12px;

  &:hover {
    text-decoration: underline;
  }
`

const Rows = styled.div`
  display: flex;
  flex-direction: column;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(96px, max-content) 1fr;
  gap: 12px;
  align-items: center;
  padding: 6px 12px;

  &:hover {
    background-color: ${(p) => p.theme.background};
  }
`

const RowLabel = styled.label`
  color: ${(p) => p.theme.text};
  font-size: 12px;
  font-weight: 500;
`

const RowControl = styled.div`
  display: flex;
  justify-content: flex-end;
  min-width: 0;
`
